import React from 'react';
import {
  AnalyticsChartType,
  AnalyticsContext,
  AnalyticsSection,
  DEFAULT_CHART_SETTINGS,
  MetricRow,
  tileSettings,
} from '../../../../support/campaignAnalytics';
import CampaignTile from './CampaignTile';
import { StatusPill, StatusTone } from '../builder/primitives';
import {
  CampaignHealthSummary,
  HealthLevel,
  HealthResult,
  HealthSignal,
} from '../../../../support/campaignHealth';
import { formatPipelineUsd } from '../../../../support/campaignLeader';
import css from './analyticsDashboard.module.css';

/** A row of headline KPI cards (one per `{label,value}`). Enterprise "at a glance" strip. */
export const KpiStrip: React.FC<{ metrics: MetricRow[] }> = ({ metrics }) => (
  <div className={css.kpiStrip}>
    {metrics.map((m) => (
      <div key={m.label} className={css.kpiCard}>
        <span className={css.kpiValue}>{m.display ?? m.value}</span>
        <span className={css.kpiLabel}>{m.label}</span>
      </div>
    ))}
  </div>
);

/** A titled card wrapping a single analytics chart tile, sized so nothing clips. When `fill` is set the
 *  chart stretches to the card's height (the card fills its grid row) rather than a fixed pixel height. */
export const ChartCard: React.FC<{
  title: string;
  chartType: AnalyticsChartType;
  data: any[];
  settings?: Record<string, any>;
  height?: number;
  full?: boolean;
  fill?: boolean;
  tileKey: string;
}> = ({ title, chartType, data, settings, height = 300, full, fill, tileKey }) => (
  <div className={`${css.card} ${full ? css.gridFull : ''}`}>
    <div className={css.cardHeader}>
      <h4 className={css.cardTitle}>{title}</h4>
    </div>
    <div className={fill ? css.cardBodyFill : css.cardBody}>
      <CampaignTile
        tileKey={tileKey}
        chartType={chartType}
        data={data}
        settings={settings ?? DEFAULT_CHART_SETTINGS}
        height={fill ? '100%' : height}
      />
    </div>
  </div>
);

/** A thin inline progress bar for a table cell / summary. */
export const ProgressCell: React.FC<{ value: number }> = ({ value }) => {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <span className={css.barCell}>
      <span className={css.barTrack}>
        <span className={css.barFill} style={{ width: `${pct}%` }} />
      </span>
      <span className={css.barPct}>{pct}%</span>
    </span>
  );
};

/**
 * Render the registry's chart tiles for a scope into the dashboard grid — the modularity seam. KPI
 * (`metricchart`) tiles are omitted here because a view surfaces those through the `KpiStrip`; every
 * other registered chart (including any a future campaign type contributes via
 * `registerCampaignAnalytics`) shows up automatically, sized so it never clips.
 */
export const RegistryCharts: React.FC<{ sections: AnalyticsSection[]; ctx: AnalyticsContext }> = ({
  sections,
  ctx,
}) => {
  const tiles = sections.flatMap((s) => s.tiles).filter((t) => t.chartType !== 'metricchart');
  if (tiles.length === 0) return null;
  return (
    <div className={css.grid}>
      {tiles.map((spec) => (
        <ChartCard
          key={spec.key}
          full={(spec.span ?? 'half') === 'full'}
          title={spec.title}
          chartType={spec.chartType}
          data={spec.compute(ctx)}
          settings={tileSettings(spec, ctx)}
          height={spec.height ?? 300}
          tileKey={spec.key}
        />
      ))}
    </div>
  );
};

// --- health --------------------------------------------------------------

const LEVEL_TONE: Record<HealthLevel, StatusTone> = {
  ok: 'done',
  attention: 'warn',
  risk: 'danger',
};
const LEVEL_LABEL: Record<HealthLevel, string> = {
  ok: 'On track',
  attention: 'Attention',
  risk: 'At risk',
};
const BANNER_CLASS: Record<HealthLevel, string> = {
  ok: css.healthOk,
  attention: css.healthAttention,
  risk: css.healthRisk,
};

/**
 * A compact health pill for a table row / inline label. Draws the eye to problems: an `ok` level renders
 * a faint dash rather than a green pill (unless `showOk`), so a table of rows highlights only the
 * attention/risk ones.
 */
export const HealthChip: React.FC<{ level: HealthLevel; label?: string; showOk?: boolean }> = ({
  level,
  label,
  showOk,
}) => {
  if (level === 'ok' && !showOk) return <span className={css.barPct}>—</span>;
  return <StatusPill tone={LEVEL_TONE[level]}>{label ?? LEVEL_LABEL[level]}</StatusPill>;
};

const SignalList: React.FC<{ signals: HealthSignal[] }> = ({ signals }) => {
  if (signals.length === 0) return null;
  return (
    <ul className={css.healthSignals}>
      {signals.map((s, i) => (
        <li key={`${s.kind}-${i}`} className={css.healthSignal}>
          <span
            className={`${css.healthDot} ${
              s.level === 'risk' ? css.healthDotRisk : css.healthDotAttention
            }`}
          />
          <span>{s.message}</span>
        </li>
      ))}
    </ul>
  );
};

/**
 * The diagnostic headline for a view. Leads with the plain-language state of *session delivery* (the
 * `headline`) plus the volume/progression metrics and attributed pipeline $ as context, then lists the
 * individual signals explaining *why*. Colour tracks the worst level. This is the "tell the story"
 * surface — it replaces reading numbers off charts to work out what's happening.
 */
export const HealthBanner: React.FC<{ summary: CampaignHealthSummary }> = ({ summary }) => (
  <div className={`${css.healthBanner} ${BANNER_CLASS[summary.level]}`}>
    <div className={css.healthTop}>
      <HealthChip level={summary.level} showOk />
      <span className={css.healthHeadline}>{summary.headline}</span>
      <div className={css.healthMetrics}>
        <div className={css.healthMetric}>
          <span className={css.healthMetricValue}>{summary.deliveryCount}</span>
          <span className={css.healthMetricLabel}>Sessions</span>
        </div>
        <div className={css.healthMetric}>
          <span className={css.healthMetricValue}>{summary.deliveriesCompleted}</span>
          <span className={css.healthMetricLabel}>Delivered</span>
        </div>
        <div className={css.healthMetric}>
          <span className={css.healthMetricValue}>{summary.deliveriesActive}</span>
          <span className={css.healthMetricLabel}>In flight</span>
        </div>
        <div className={css.healthMetric}>
          <span className={css.healthMetricValue}>{formatPipelineUsd(summary.pipelineTotal)}</span>
          <span className={css.healthMetricLabel}>Attributed $</span>
        </div>
      </div>
    </div>
    <SignalList signals={summary.signals} />
  </div>
);

/** A "banner" for a single delivery/row-level `HealthResult` (signals only, no volume metrics). */
export const HealthNote: React.FC<{ health: HealthResult }> = ({ health }) => {
  if (health.signals.length === 0) return null;
  return (
    <div className={`${css.healthBanner} ${BANNER_CLASS[health.level]}`}>
      <SignalList signals={health.signals} />
    </div>
  );
};

/** The explicit "this row drills in" affordance for a MasterTable trailing cell. */
export const ViewLink: React.FC<{ label?: string }> = ({ label = 'View' }) => (
  <span className={css.viewLink}>{label} →</span>
);

export type Column<T> = {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  numeric?: boolean;
};

/**
 * A generic, clickable master table with a sticky header that scrolls (never clips). Rows are
 * selectable/drillable when `onRowClick` is given; the active row is highlighted. This is the primary
 * interactive surface of the dashboard — pick a campaign, delivery, or customer to drill in.
 */
export function MasterTable<T>({
  columns,
  rows,
  getRowId,
  onRowClick,
  selectedId,
  empty = 'Nothing to show yet.',
}: {
  columns: Column<T>[];
  rows: T[];
  getRowId: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  selectedId?: string | number;
  empty?: React.ReactNode;
}): JSX.Element {
  if (rows.length === 0) return <div className={css.empty}>{empty}</div>;
  return (
    <div className={css.tableWrap}>
      <table className={css.table}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={c.numeric ? css.numeric : undefined}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const id = getRowId(row);
            const selected = selectedId != null && id === selectedId;
            return (
              <tr
                key={id}
                className={`${onRowClick ? css.rowClickable : ''} ${selected ? css.rowSelected : ''}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((c) => (
                  <td key={c.key} className={c.numeric ? css.numeric : undefined}>
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** A titled panel that holds a master table or detail content. `subtitle` adds a hint line under the
 *  title (e.g. "Click a session to drill in") so the surface explains itself. */
export const Panel: React.FC<{
  title: string;
  subtitle?: string;
  count?: number;
  actions?: React.ReactNode;
  flush?: boolean;
  full?: boolean;
  children: React.ReactNode;
}> = ({ title, subtitle, count, actions, flush, full, children }) => (
  <div className={`${css.card} ${full ? css.gridFull : ''}`}>
    <div className={css.cardHeader}>
      <div className={css.cardHeaderCol}>
        <h4 className={css.cardTitle}>
          {title}
          {count != null && <span className={css.cardCount}> · {count}</span>}
        </h4>
        {subtitle && <p className={css.cardSubtitle}>{subtitle}</p>}
      </div>
      {actions}
    </div>
    <div className={flush ? css.cardBodyFlush : css.cardBody}>{children}</div>
  </div>
);

/** A labelled dropdown for a drill dimension (e.g. Sales Level 1). Light-themed to match the dashboard. */
export const DrillSelect: React.FC<{
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ label, value, options, onChange, disabled }) => (
  <label className={css.drillField}>
    <span className={css.drillLabel}>{label}</span>
    <select
      className={css.drillSelect}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </label>
);

/** A horizontal container for one or more `DrillSelect`s. */
export const DrillBar: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={css.drillBar}>{children}</div>
);

export type Crumb = { label: string; onClick?: () => void };

/** Clickable drill-path breadcrumb: Program › Campaign › Delivery › Customer. */
export const Breadcrumbs: React.FC<{ items: Crumb[] }> = ({ items }) => (
  <nav className={css.crumbs} aria-label="Drill path">
    {items.map((c, i) => {
      const last = i === items.length - 1;
      return (
        <React.Fragment key={`${c.label}-${i}`}>
          {i > 0 && <span className={css.crumbSep}>›</span>}
          <button
            type="button"
            className={`${css.crumb} ${last ? css.crumbActive : ''}`}
            onClick={last ? undefined : c.onClick}
            disabled={last}
            title={c.label}
          >
            {c.label}
          </button>
        </React.Fragment>
      );
    })}
  </nav>
);

export { css as dashboardCss };
